import { useRef, useState } from "react";
import ChatSessionsContext from "./ChatSessionsContext";
import { Outlet } from "react-router-dom";
import { ChatSession, Message } from "../interfaces/Chat";
import { useChat } from "../hooks/UseChat";

export function ChatSessionsContextProvider() {
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [selectedChatSession, setSelectedChatSession] = useState<
    string | null
  >();
  const [messages, setMessages] = useState<Message[]>([]);
  const noMsgReset = useRef(false);
  const [filterFromYear, setFilterFromYear] = useState<number | null>(null);
  const [filterToYear, setFilterToYear] = useState<number | null>(null);
  const [filterDocumentTypes, setFilterDocumentTypes] = useState<string[]>([]);
  const [filterAuthor, setFilterAuthor] = useState<string | null>(null);

  const { createNewChatSession } = useChat();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const streamGenerationRef = useRef(0);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    console.log("isClosing", isClosing);
    if (!isClosing) {
      console.log("setMobileOpen", !mobileOpen);
      setMobileOpen(!mobileOpen);
    }
  };

  const updateSelectedChatSession = (chatSessionId: string) => {
    setSelectedChatSession(chatSessionId);
  };

  const addNewChatSession = (question?: string) => {
    const newChatSession = createNewChatSession(question);

    setChatSessions((prevChatSessions) => [
      newChatSession,
      ...prevChatSessions,
    ]);

    if (!noMsgReset.current) setMessages([]);
    setSelectedChatSession(newChatSession.chatInfo.chatId);
    return newChatSession.chatInfo.chatId;
  };

  const updateNewChatSession = (question: string) => {
    // update the chat session title if it is a new chat session
    const updatedChatSessions = [...chatSessions];
    updatedChatSessions[0] = {
      ...updatedChatSessions[0],
      chatInfo: {
        ...updatedChatSessions[0].chatInfo,
        chatTitle: question,
      },
    };
    setChatSessions(updatedChatSessions);
  };

  const updateChatSessionTitle = (chatId: string, newTitle: string) => {
    // Use functional state update to ensure we have the latest state
    setChatSessions((prevChatSessions) => {
      console.log(
        `Current chatSessions:`,
        prevChatSessions.map((s) => ({
          id: s.chatInfo.chatId,
          title: s.chatInfo.chatTitle,
        }))
      );

      const updatedChatSessions = prevChatSessions.map((session) => {
        if (session.chatInfo.chatId === chatId) {
          console.log(
            `Found matching session, updating title from "${session.chatInfo.chatTitle}" to "${newTitle}"`
          );
          return {
            ...session,
            chatInfo: {
              ...session.chatInfo,
              chatTitle: newTitle,
            },
          };
        }
        return session;
      });

      console.log(
        `Updated chatSessions:`,
        updatedChatSessions.map((s) => ({
          id: s.chatInfo.chatId,
          title: s.chatInfo.chatTitle,
        }))
      );
      console.log(
        `Sessions count - before: ${prevChatSessions.length}, after: ${updatedChatSessions.length}`
      );
      return updatedChatSessions;
    });
  };

  const resetSelectedChatSession = () => {
    setSelectedChatSession(null);
    setMessages([]);
  };

  // Bump generation so only the latest stream may update the current UI.
  // Prior SSE requests keep running in the background (title/persistence).
  const beginChatStream = () => {
    streamGenerationRef.current += 1;
    return { generation: streamGenerationRef.current };
  };

  const invalidateActiveStream = () => {
    streamGenerationRef.current += 1;
  };

  const isStreamGenerationActive = (generation: number) =>
    generation === streamGenerationRef.current;

  return (
    <ChatSessionsContext.Provider
      value={{
        chatSessions,
        updateSelectedChatSession,
        addNewChatSession,
        updateNewChatSession,
        updateChatSessionTitle,
        resetSelectedChatSession,
        beginChatStream,
        invalidateActiveStream,
        isStreamGenerationActive,
        setChatSessions,
        selectedChatSession,
        messages,
        setMessages,
        noMsgReset,
        filterFromYear: filterFromYear,
        setFilterToYear,
        filterToYear: filterToYear,
        setFilterFromYear,
        filterDocumentTypes,
        setFilterDocumentTypes,
        filterAuthor,
        setFilterAuthor,
        mobileOpen,
        handleDrawerToggle,
        handleDrawerTransitionEnd,
        handleDrawerClose,
        inputRef,
      }}
    >
      <Outlet />
    </ChatSessionsContext.Provider>
  );
}
